/**
 * Notches v1 — frontend service.
 *
 * Talks to GET /notches/signals (auth-gated by user.notchesEnabled).
 * Backend Lambda: notches-api-get-signals (reads notches-signals-v1).
 */

import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ─── Types ────────────────────────────────────────────────────────────────────

export type NotchesSymbol = 'SPX' | 'SPY' | 'QQQ' | 'IWM' | 'ES' | 'MES' | 'GC' | 'CL' | 'NQ' | 'MNQ';
export type NotchesPreset = 'conservative' | 'balanced' | 'aggressive';

export interface NotchesCandidate {
  strike: number;
  expiration: string;
  dte: number;
  settlement?: string;
  // Client-side annotation only — attached by NotchesPage.tsx's mixedCandidates
  // memo (Notches mixed-table plan Task 2.3) to tag each candidate with the
  // strategy it came from. Never sent by the API; safe to be undefined on any
  // candidate that came in through the primary `data.candidates` path without
  // being re-annotated.
  _strategy?: string;
  // Multi-leg strike fields — populated only for the 4 new (Task 5.1/5.2/5.3)
  // strategies; naked_put candidates never carry these. Field-name convention
  // per generator: bull_put_spread/bear_call_spread use short_strike +
  // long_strike; jade_lizard uses short_put_strike/short_call_strike/
  // long_call_strike; iron_condor uses put_short_strike/put_long_strike/
  // call_short_strike/call_long_strike (per iron_condor_rules.py:82-85).
  short_strike?: number;
  long_strike?: number;
  short_put_strike?: number;
  short_call_strike?: number;
  long_call_strike?: number;
  put_short_strike?: number;
  put_long_strike?: number;
  call_short_strike?: number;
  call_long_strike?: number;
  delta: number;
  gamma: number;
  theta: number;
  vega: number;
  iv_pct: number;
  bid: number;
  ask: number;
  credit: number;
  credit_per_day: number;
  points_from_atm: number;
  prob_profit_pct: number;
  sharpe_annualized: number;
  eligible_presets: NotchesPreset[];
  scores: { conservative?: number; balanced?: number; aggressive?: number };
  ranks: { conservative?: number; balanced?: number; aggressive?: number };
  notches_score?: number | null;
  notches_rank?: number | null;
  exit_recommendation?: {
    universal?: any;
    per_preset?: Record<NotchesPreset, {
      profit_target_credit: number;
      profit_target_pct: number;
      hard_stop_credit: number;
      hard_stop_multiple: number;
      hard_stop_underlying: number | null;
      underlying_buffer_pct: number;
      trail_mult_effective: number;
      trail_mult_base: number;
      trail_mult_adjustment: number;
      expected_hold_days: number;
      max_hold_days: number;
    }>;
  };
}

export interface NotchesMacroFactor {
  symbol: string;
  regime: 'calm' | 'cautious' | 'stressed' | 'extreme' | 'unknown';
  deviation_sigma: number;
  today_close: number | null;
  mean_5d: number | null;
  stdev_5d: number | null;
  penalty: number;
  futures_primary?: string;
}

export interface NotchesSkew {
  skew_ratio: number;
  skew_penalty: number;
  skew_regime: string;
  vix?: number | null;
  rolling_avg?: number | null;
  skew_change?: number | null;
  regime_boosted?: boolean;
  expirations_measured?: number;
}

export interface NotchesSnapshotMeta {
  snapshot_time: string;
  collected_at?: string | null;  // wall-clock chain-completion time; finer-grained than the hour-bucketed snapshot_time
  spot_price: number;
}

export type RegimeMode = 'disabled' | 'shadow' | 'active' | string;

// Snapshot-wide regime debug payload. Shape comes from
// notches-signals/notches-framework/pkg/regime_apply.py.
// All fields except `mode` are optional because disabled / missing-state
// paths short-circuit before filling them in.
export interface RegimeDebug {
  mode: RegimeMode;
  missing?: boolean;
  base?: number;
  adjusted?: number;
  adjustment?: number;
  weight?: number;
  gex_tag?: string | null;
  cot_tag?: string | null;
}

export interface NotchesSnapshot {
  symbol: NotchesSymbol;
  snapshot_time: string;
  collected_at?: string | null;  // wall-clock chain-completion time; finer-grained than the hour-bucketed snapshot_time
  date: string;
  strategy: string;
  spot_price: number;
  vix_level: number | null;
  total_candidates_pre_filter: number;
  total_candidates: number;
  preset_eligible_counts: { conservative: number; balanced: number; aggressive: number };
  preset_delta_caps: { conservative: number; balanced: number; aggressive: number };
  weight_presets: Record<NotchesPreset, Record<string, number>>;
  combined_penalty: number;
  skew: NotchesSkew | null;
  macro: {
    oil?: NotchesMacroFactor;
    gold?: NotchesMacroFactor;
    bonds?: NotchesMacroFactor;
    rates?: NotchesMacroFactor;
    combined_penalty: number;
  };
  claude_analysis?: { summary?: string; model?: string; analyzed_at?: string };
  chatgpt_analysis?: { summary?: string; model?: string; analyzed_at?: string };
  preset: NotchesPreset;
  candidates: NotchesCandidate[];
  model_version: string;
  snapshots?: NotchesSnapshotMeta[];
  // Regime service (Phase 4). Snapshot-wide for the single-strategy SPX/SPY/ES/MES
  // engines — every candidate shares the same regime_debug because each snapshot
  // is one symbol + one strategy. `regime_applied=true` ONLY when the engine is
  // in `active` mode; in `shadow` mode `regime_debug` is populated for visibility
  // but `regime_applied=false` because the score wasn't actually shifted.
  regime_applied?: boolean;
  regime_debug?: RegimeDebug | null;
  // Notches mixed-table plan Task 1.1: additive per-strategy snapshot map.
  // Only populated when the caller passes `include: 'all-strategies'`. Each
  // key holds a trimmed snapshot (candidates + snapshot_time + total_candidates)
  // for that strategy, or null when no row exists for the date. Used by the
  // mixed-table NotchesPage view to interleave candidates from all 4
  // strategies without needing 4 separate API calls.
  strategies?: Record<string, {
    strategy: string;
    snapshot_time: string;
    collected_at?: string | null;
    spot_price: number;
    total_candidates: number;
    total_candidates_pre_filter?: number;
    candidates: NotchesCandidate[];
    // Per-strategy AI analysis (Claude + ChatGPT). Each strategy runs its
    // own scoped prompt per C6/C7 of the multi-strategy rebuild plan. Field
    // shapes intentionally loose (`any`) because BCS/jade/iron produce
    // different keys than naked_put (e.g. `ranked_picks` vs `best_per_band`,
    // `short_strike` vs `strike`).
    claude_analysis?: any | null;
    chatgpt_analysis?: any | null;
  } | null>;
}

export interface NotchesEmptyResponse {
  symbol: NotchesSymbol;
  date: string;
  preset: NotchesPreset;
  empty: true;
  message: string;
  snapshots?: NotchesSnapshotMeta[];
}

export type NotchesResponse = NotchesSnapshot | NotchesEmptyResponse;

export const isEmpty = (r: NotchesResponse): r is NotchesEmptyResponse =>
  (r as NotchesEmptyResponse).empty === true;

// ─── Audit types ─────────────────────────────────────────────────────────────

export type NotchesAuditStatus = 'success' | 'failure' | 'skip' | 'warning';

export interface NotchesAuditEvent {
  run_date: string;
  event_id: string;
  run_id: string;
  snapshot_time: string;
  symbol: NotchesSymbol;
  sequence: number;
  event_type: string;
  step: string;
  status: NotchesAuditStatus;
  summary: string | null;
  detail: Record<string, any> | null;
  duration_ms: number | null;
  timestamp: string;
}

export interface NotchesAuditStepSummary {
  step: string;
  status: NotchesAuditStatus;
  event_type: string;
  duration_ms: number | null;
}

export interface NotchesAuditRunSummary {
  runId: string;
  snapshotTime: string;
  symbol: NotchesSymbol;
  startedAt: string | null;
  completedAt: string | null;
  totalEvents: number;
  totalDurationMs: number | null;
  steps: NotchesAuditStepSummary[];
  errors: number;
  status: 'success' | 'error';
}

// ─── Stress-test types ───────────────────────────────────────────────────────

export type StressTriggerStatus = 'hold' | 'defend' | 'close';

export type StressTriggerLevel = 'ALERT_ONLY' | 'SEMI_AUTO' | 'FULL_AUTO';
export type StressChannel = 'sms' | 'email' | 'ui';
export type StressActionType = 'CLOSE_INTENT' | 'ALERT';

export interface StressTriggerPlaybook {
  actions: { type: StressActionType; tradeId?: string; params?: Record<string, any> }[];
}

export interface StressTriggerRecord {
  userId: string;
  triggerId: string;
  name: string;
  condition: string;
  level: StressTriggerLevel;
  channels: StressChannel[];
  cooldownMinutes: number;
  enabled: boolean;
  engineId?: string;
  alertMessage?: string | null;
  playbook?: StressTriggerPlaybook;
  lastFiredAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface StressTriggerCreateInput {
  name: string;
  condition: string;
  level: StressTriggerLevel;
  channels?: StressChannel[];
  cooldownMinutes?: number;
  enabled?: boolean;
  engineId?: string;
  alertMessage?: string | null;
  playbook?: StressTriggerPlaybook;
}

export type StressTriggerUpdateInput = Partial<StressTriggerCreateInput>;

export interface StressImpactRow {
  tradeId: string;
  engineId: string;
  symbol: string;
  scenarioKey: string;
  scenarioName: string;
  strategy: 'naked_put' | 'credit_spread' | null;
  shortStrike: number | null;
  longStrike: number | null;
  expiration: string | null;
  quantity: number | null;
  openedCredit: number | null;
  maxLossDollars: number | null;
  bsProjectedPnl: number | null;
  bsSpreadValue: number | null;
  greeksProjectedPnl: number | null;
  greeksSpreadValue: number | null;
  shockedUnderlying: number | null;
  shockedShortIv: number | null;
  shockedShortDte: number | null;
  // Skew-aware IV shock breakdown (Phase B+: per-position IV amplification)
  skewRegime: string | null;
  skewRatio: number | null;
  rawIvShock: number | null;             // scenario's flat iv_shock_pts (decimal)
  amplifiedShortIvShock: number | null;  // actually-applied iv shock on short leg
  shortRegimeFactor: number | null;
  shortDeltaFactor: number | null;
  triggerStatus: StressTriggerStatus;
  spotSource: string;
  computedAt: string;
}

export interface StressScenarioParams {
  price_shock_pct: number;
  iv_shock_pts: number;
  days_forward: number;
  vix_shock_pts?: number;
}

export interface StressScenarioRecord {
  scenarioId: string;
  name: string;
  description?: string;
  params: StressScenarioParams;
  readonly: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface StressScenarioInput {
  name: string;
  description?: string;
  params: StressScenarioParams;
}

export interface StressImpactsResponse {
  userId: string;
  cacheHit: boolean;
  computedAt: string | null;
  rows: StressImpactRow[];
}

// ─── API ──────────────────────────────────────────────────────────────────────

export const notchesService = {
  getSignals: async (
    token: string,
    symbol: NotchesSymbol,
    preset: NotchesPreset = 'balanced',
    date?: string,
    snapshotTime?: string,
    include?: string,
  ): Promise<NotchesResponse> => {
    const params: Record<string, string> = { symbol, preset };
    if (date) params.date = date;
    if (snapshotTime) params.snapshot_time = snapshotTime;
    // Notches mixed-table plan Task 2.1: opt into the additive `strategies`
    // response field. Callers that omit this param see the exact same response
    // shape as before.
    if (include) params.include = include;
    const response = await api.get<NotchesResponse>('/notches/signals', {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  getAuditRuns: async (
    token: string,
    symbol?: NotchesSymbol,
    date?: string,
  ): Promise<{ date: string; runs: NotchesAuditRunSummary[]; events: NotchesAuditEvent[] }> => {
    const params: Record<string, string> = {};
    if (symbol) params.symbol = symbol;
    if (date) params.date = date;
    const response = await api.get('/notches/audit', {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  getAuditRunEvents: async (
    token: string,
    runId: string,
  ): Promise<{ events: NotchesAuditEvent[] }> => {
    const response = await api.get('/notches/audit', {
      params: { runId },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // Stress-test impact matrix (Phase B/C of TT auto-trader stress test)
  // See docs/wiki/decisions/2026-05-14-tt-stress-test.md
  getStressImpacts: async (
    token: string,
    opts: { engineId?: string; refresh?: boolean } = {},
  ): Promise<StressImpactsResponse> => {
    const params: Record<string, string> = {};
    if (opts.engineId) params.engineId = opts.engineId;
    if (opts.refresh) params.refresh = '1';
    const response = await api.get<StressImpactsResponse>(
      '/notches/stress-impacts',
      { params, headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  },

  // ── Stress-test triggers (Phase E) ─────────────────────────────────────────
  listStressTriggers: async (token: string): Promise<{ triggers: StressTriggerRecord[] }> => {
    const response = await api.get('/notches/stress-triggers', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  createStressTrigger: async (token: string, body: StressTriggerCreateInput): Promise<{ trigger: StressTriggerRecord }> => {
    const response = await api.post('/notches/stress-triggers', body, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  updateStressTrigger: async (token: string, triggerId: string, body: StressTriggerUpdateInput): Promise<{ trigger: StressTriggerRecord }> => {
    const response = await api.put(`/notches/stress-triggers/${triggerId}`, body, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  deleteStressTrigger: async (token: string, triggerId: string): Promise<void> => {
    await api.delete(`/notches/stress-triggers/${triggerId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  executeStressTrigger: async (token: string, triggerId: string, body: { tradeId?: string } = {}): Promise<{ accepted: boolean; message: string; eventId?: string }> => {
    const response = await api.post(`/notches/stress-triggers/${triggerId}/execute`, body, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // ── Stress-test scenarios (custom what-if conditions) ─────────────────────
  listStressScenarios: async (token: string): Promise<{ scenarios: StressScenarioRecord[] }> => {
    const response = await api.get('/notches/stress-scenarios', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  createStressScenario: async (token: string, body: StressScenarioInput): Promise<{ scenario: StressScenarioRecord }> => {
    const response = await api.post('/notches/stress-scenarios', body, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  updateStressScenario: async (token: string, scenarioId: string, body: Partial<StressScenarioInput>): Promise<{ scenario: StressScenarioRecord }> => {
    const response = await api.put(`/notches/stress-scenarios/${scenarioId}`, body, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  deleteStressScenario: async (token: string, scenarioId: string): Promise<void> => {
    await api.delete(`/notches/stress-scenarios/${scenarioId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  // ── Risk briefings (per-candidate AI brief) ───────────────────────────────
  // When `snapshotTime` is omitted, the API returns briefings for the latest
  // snapshot. Pass an explicit snapshotTime to pin briefings to the same
  // snapshot the user has selected in the signals view.
  getRiskBriefings: async (token: string, symbol: NotchesSymbol, snapshotTime?: string | null): Promise<{
    symbol: NotchesSymbol;
    empty?: boolean;
    snapshotTime: string | null;
    generatedAt: string | null;
    modelVersion?: string | null;
    briefings: {
      candidateKey: string;
      symbol: string;
      briefing: string;
      riskLevel: 'low' | 'medium' | 'high';
      watchFor: string;
      snapshotTime: string;
      date: string;
      generatedAt: string;
      modelVersion: string;
    }[];
  }> => {
    const params: Record<string, string> = { symbol };
    if (snapshotTime) params.snapshotTime = snapshotTime;
    const response = await api.get('/notches/risk-briefings', {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  refreshRiskBriefings: async (token: string, symbol?: NotchesSymbol): Promise<{ refreshed: boolean; totalWritten: number; results: any[] }> => {
    const body = symbol ? { symbol } : {};
    const response = await api.post('/notches/risk-briefings/refresh', body, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 60000,
    });
    return response.data;
  },

  suggestStressScenario: async (token: string, description: string): Promise<{
    suggestion: {
      name: string;
      description: string;
      params: StressScenarioParams;
      reasoning: string;
    };
  }> => {
    const response = await api.post('/notches/stress-scenarios/suggest', { description }, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 25000,
    });
    return response.data;
  },
};
